// PRD.278 follow-up — automatic Elia context switching.
//
// The single source of the effective Elia scope. Derives the route scope from
// the active patient-workspace route (resolving patient_encounters.visit_id for
// visit routes — NEVER the encounter row id), tracks a manual Change override
// that is cleared whenever the route scope changes, and exposes the patient's
// visits for the Change selector.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  qEncounterById,
  qVisitsByPatient,
  type PatientEncounterRow,
  type PatientVisitRow,
} from '@helios/data-access';
import { usePatientTabs } from '../PatientTabsContext';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { parsePatientRoute, resolveEffectiveScope, scopeKey, type EliaScope } from './elia-scope';

export interface EliaScopeVisitOption {
  id: string;
  visitDate: string;
}

export interface EliaScopeController {
  scope: EliaScope | null;
  patientName?: string;
  availableVisits: EliaScopeVisitOption[];
  /** Set a manual override (Change control). Cross-patient scopes are ignored. */
  setManualScope: (scope: EliaScope) => void;
}

export function useEliaEffectiveScope(): EliaScopeController {
  const { pathname } = useLocation();
  const { tabs, activePatientId } = usePatientTabs();

  const route = parsePatientRoute(pathname);
  // Route patient wins; fall back to the active workspace tab.
  const patientId = route.patientId ?? activePatientId ?? undefined;
  const encounterId = route.encounterId;

  // Resolve the encounter → its clinical visit id (guarded to the same patient).
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const encounter = encounterId ? encounterRows[0] : undefined;
  const visitId = encounter && encounter.patient_id === patientId ? encounter.visit_id : undefined;

  // Patient's visits — for the visit date label and the Change selector.
  const { data: visitRows = [] } = usePowerSyncQuery<PatientVisitRow>(
    qVisitsByPatient(patientId ?? ''),
  );
  const visitDate = visitId ? visitRows.find((v) => v.id === visitId)?.visit_date : undefined;

  const routeScope: EliaScope | null = patientId
    ? visitId
      ? { kind: 'visit', patientId, visitId, visitDate }
      : { kind: 'patient', patientId }
    : null;
  const routeKey = scopeKey(routeScope);

  // Manual override, cleared whenever the ROUTE scope changes (patient or visit).
  const [manualOverride, setManualOverride] = useState<EliaScope | null>(null);
  const prevRouteKey = useRef(routeKey);
  useEffect(() => {
    if (prevRouteKey.current !== routeKey) {
      setManualOverride(null);
      prevRouteKey.current = routeKey;
    }
  }, [routeKey]);

  const effectiveScope = resolveEffectiveScope(routeScope, manualOverride);

  const active = patientId ? tabs.find((t) => t.id === patientId) : undefined;
  const patientName = active?.fullName || active?.label || undefined;
  const availableVisits: EliaScopeVisitOption[] = visitRows.map((v) => ({
    id: v.id,
    visitDate: v.visit_date,
  }));

  const setManualScope = useCallback(
    (scope: EliaScope) => {
      // Never allow a scope for a different patient than the current route.
      if (routeScope && scope.patientId !== routeScope.patientId) return;
      setManualOverride(scope);
    },
    [routeScope?.patientId],
  );

  return { scope: effectiveScope, patientName, availableVisits, setManualScope };
}
